import React, { useState, useContext } from 'react';
import { AppContext } from '../context/AppContext';
import { 
  ShieldCheck, 
  Lock, 
  CheckCircle2, 
  Building2, 
  Calendar, 
  FileText, 
  Download, 
  Printer, 
  RotateCcw, 
  UserCheck, 
  Clock, 
  Check, 
  X, 
  AlertCircle,
  MessageSquare,
  Paperclip,
  Eye,
  EyeOff
} from 'lucide-react';

import { downloadContractPdf, printContractDocument } from '../utils/contractPdfHelper';

const ContractDocumentView = ({ 
  contract, 
  isPrintMode = false, 
  hideInlineApproval = false,
  onOpenWithdrawModal 
}) => {
  const { currentUser, companySeal, approveResignationStep, rejectResignationStep } = useContext(AppContext);
  const [approvalComment, setApprovalComment] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  if (!contract) return null;

  const isFreelancer = contract.TargetType === 'FREELANCER';
  const isLabor = contract.ContractType === 'LABOR';
  const isSalary = contract.ContractType === 'SALARY';
  const isNda = contract.ContractType === 'NDA' || contract.ContractType === 'RESIGNATION_NDA';
  const isResignation = contract.ContractType === 'RESIGNATION';
  const isHandover = contract.ContractType === 'HANDOVER';

  // Format currency
  const formatWon = (num) => (num ? Number(num).toLocaleString('ko-KR') : '0');

  // Mask resident number / phone
  const maskedPhone = contract.UserPhone ? contract.UserPhone.replace(/(\d{3})-(\d{2})\d{2}-(\d{4})/, '$1-$2**-$3') : '-';

  // Check if compensation/salary is applicable
  const hasCompensation = (contract.AnnualSalary > 0 || contract.TotalAmount > 0) && !isNda && !isResignation && !isHandover && contract.ContractType !== 'PRIVACY';

  // Determine badge text and style
  let typeBadgeLabel = '정규 인사 전자 계약';
  let typeBadgeStyle = 'bg-indigo-50 text-indigo-700 border-indigo-200';
  if (isFreelancer) {
    typeBadgeLabel = '외부 전문 용역 도급 계약';
    typeBadgeStyle = 'bg-sky-50 text-sky-700 border-sky-200';
  } else if (isResignation) {
    typeBadgeLabel = '표준 사직서 (퇴직원)';
    typeBadgeStyle = 'bg-rose-50 text-rose-700 border-rose-200';
  } else if (contract.ContractType === 'RESIGNATION_NDA') {
    typeBadgeLabel = '퇴사자 보안 및 비밀유지 서약';
    typeBadgeStyle = 'bg-amber-50 text-amber-800 border-amber-200';
  } else if (isHandover) {
    typeBadgeLabel = '업무 및 자산 인수인계 확인';
    typeBadgeStyle = 'bg-emerald-50 text-emerald-700 border-emerald-200';
  } else if (isNda) {
    typeBadgeLabel = '보안 및 정보보호 서약';
    typeBadgeStyle = 'bg-slate-100 text-slate-700 border-slate-200';
  }

  // Company seal configuration (Prioritizes contract snapshot, then global companySeal, defaults to clouflake.png)
  const activeSeal = contract?.CompanySeal || companySeal;
  const compName = activeSeal?.companyName || '주식회사 클로우플레이크';
  const repName = activeSeal?.representativeName || '대표이사 이용관';
  const compAddress = activeSeal?.address || '서울시 강서구 양천로 354 옥천빌딩 9층 클로우플레이크 본사';
  const sealText = activeSeal?.sealText || '클로우\n플레이크\n대표이사';
  const sealShape = activeSeal?.shape || 'CIRCLE';
  const sealImage = activeSeal?.imageUrl || (activeSeal?.type === 'CUSTOM_IMAGE' ? activeSeal?.imageUrl : null) || '/clouflake.png';

  // Resident registration number & address visibility
  const rawRrn = contract.ResidentRegistrationNumber || contract.residentNumber || '';
  const rawAddress = contract.UserAddress || contract.userAddress || '';

  const isAdmin = Boolean(
    currentUser?.Role === 'ADMIN' || 
    currentUser?.UserID === 'emp-09' || 
    currentUser?.UserID === 'emp-10' || 
    currentUser?.UserID === 'clou0315' ||
    (currentUser?.Position && (currentUser.Position.includes('대표') || currentUser.Position.includes('이사'))) ||
    currentUser?.Department === '경영지원' ||
    currentUser?.Department === '인사'
  );

  const isOwner = Boolean(
    currentUser && (
      (currentUser.UserID && contract.UserID && currentUser.UserID === contract.UserID) ||
      (currentUser.Name && contract.UserName && currentUser.Name.trim() === contract.UserName.trim()) ||
      (currentUser.Email && contract.UserEmail && currentUser.Email.toLowerCase() === contract.UserEmail.toLowerCase())
    )
  );

  const canToggleRrn = Boolean(isAdmin || isOwner);

  // Admin & Owner can see the full resident number by default
  const [showResidentNumber, setShowResidentNumber] = useState(() => Boolean(isAdmin || isOwner));

  // Sync when currentUser loads
  React.useEffect(() => {
    if (isAdmin || isOwner) {
      setShowResidentNumber(true);
    }
  }, [isAdmin, isOwner]);

  // Format resident number (Masked by default: 950101-1******)
  const formatResidentNumber = (rrn, isRevealed) => {
    if (!rrn) return '-';
    const clean = rrn.replace(/[^0-9]/g, '');
    if (clean.length < 6) return rrn;
    const front = clean.slice(0, 6);
    const back = clean.slice(6);
    if (isRevealed && !isPrintMode) {
      return back ? `${front}-${back}` : front;
    }
    // Default masked mode
    const firstDigit = back ? back[0] : '';
    return back ? `${front}-${firstDigit}******` : `${front}-*******`;
  };

  return (
    <div className={`bg-white text-slate-900 font-sans ${isPrintMode ? 'p-0' : 'p-6 md:p-10 border border-slate-200 rounded-2xl shadow-sm'} max-w-4xl mx-auto text-left relative`}>
      {!isPrintMode && (
        <div className="no-print mb-4 flex items-center justify-between bg-slate-50 p-3 rounded-xl border border-slate-200">
          <span className="text-xs text-slate-600 font-bold flex items-center gap-1.5">
            <FileText className="w-4 h-4 text-indigo-600" />
            <span>전자계약서 인쇄 / PDF 다운로드</span>
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => downloadContractPdf(document.getElementById(`contract-doc-${contract.ContractID}`), contract.Title)}
              className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold transition-all shadow-2xs cursor-pointer"
              title="PDF 파일 다운로드"
            >
              <Download className="w-3.5 h-3.5" />
              <span>PDF 다운로드</span>
            </button>
            <button
              type="button"
              onClick={() => printContractDocument(document.getElementById(`contract-doc-${contract.ContractID}`), contract.Title)}
              className="flex items-center gap-1 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold transition-all shadow-2xs cursor-pointer"
              title="A4 다중 페이지 출력"
            >
              <Printer className="w-3.5 h-3.5" />
              <span>PDF / 인쇄</span>
            </button>
          </div>
        </div>
      )}

      <div id={`contract-doc-${contract.ContractID}`} className="print-contract-target">
        {/* Official Security Header */}
        <div className="border-b-2 border-slate-900 pb-4 mb-6 flex items-center justify-between avoid-break">
          <div>
            <span className={`text-[11px] font-bold tracking-widest uppercase border px-2.5 py-0.5 rounded ${typeBadgeStyle}`}>
              {typeBadgeLabel}
            </span>
            <h1 className="text-xl md:text-2xl font-black text-slate-900 mt-2 tracking-tight">
              {contract.Title}
            </h1>
          </div>
          <div className="text-right text-xs text-slate-500 font-mono">
            <div>문서번호: DOC-{contract.ContractID}</div>
            <div>작성일자: {contract.CreatedAt ? contract.CreatedAt.slice(0, 10) : '-'}</div>
          </div>
        </div>

      {/* Intro clause */}
      <div className="text-xs md:text-sm text-slate-700 leading-relaxed mb-6 bg-slate-50 p-4 rounded-xl border border-slate-200">
        {isResignation ? (
          <p>
            상기 본인 <strong className="text-rose-700">{contract.UserName}</strong>은(는) 아래와 같은 사유로 인하여{' '}
            <strong className="text-slate-900">주식회사 클로우플레이크</strong>에 사직원(사직서)을 정식 제출하며, 퇴사일까지 인수인계를 성실히 완수하고 사내 제반 규정을 준수할 것을 확약합니다.
          </p>
        ) : isNda ? (
          <p>
            <strong className="text-slate-900">주식회사 클로우플레이크</strong> (이하 "회사"라 한다)와{' '}
            <strong className="text-indigo-700">{contract.UserName}</strong> (이하 "서약인"이라 한다)는 회사의 지식재산권 및 영업비밀을 보호하기 위하여 다음과 같이 서약서를 체결한다.
          </p>
        ) : isHandover ? (
          <p>
            <strong className="text-slate-900">주식회사 클로우플레이크</strong> 소속{' '}
            <strong className="text-emerald-700">{contract.UserName}</strong>은(는) 퇴직에 따라 담당하던 업무 및 사내 자산 인수인계 내역을 다음과 같이 성실히 이관 완료하였음을 확인합니다.
          </p>
        ) : (
          <p>
            <strong className="text-slate-900">주식회사 클로우플레이크</strong> (이하 "회사"라 한다)와{' '}
            <strong className="text-indigo-700">{contract.UserName}</strong> (이하 "{isFreelancer ? '수급인' : '근로자'}"이라 한다)는 상호 신의성실의 원칙에 따라 다음과 같이 계약을 체결하고 이를 성실히 이행할 것을 확약한다.
          </p>
        )}
      </div>

      {/* Contract Core Info Table */}
      <div className="mb-6 overflow-x-auto">
        <h2 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
          <FileText className="w-3.5 h-3.5 text-indigo-600" />
          제 1 장 : {isResignation ? '신청자 인적사항 및 퇴직 정보' : '기본 계약 정보'}
        </h2>
        <table className="w-full text-xs border-collapse border border-slate-200">
          <tbody>
            <tr className="border-b border-slate-200">
              <th className="bg-slate-100 p-2.5 text-slate-700 font-bold w-1/4 border-r border-slate-200">
                {isFreelancer ? '수급인(성명)' : isNda ? '서약인(성명)' : isResignation ? '신청인 (퇴직자 성명)' : '근로자(성명)'}
              </th>
              <td className="p-2.5 font-bold text-slate-900 w-1/4 border-r border-slate-200">
                {contract.UserName}
              </td>
              <th className="bg-slate-100 p-2.5 text-slate-700 font-bold w-1/4 border-r border-slate-200">
                연락처
              </th>
              <td className="p-2.5 text-slate-800 w-1/4">
                {contract.UserPhone || '-'}
              </td>
            </tr>
            <tr className="border-b border-slate-200">
              <th className="bg-slate-100 p-2.5 text-slate-700 font-bold border-r border-slate-200">
                주민등록번호
              </th>
              <td className="p-2.5 font-mono text-slate-900 border-r border-slate-200">
                {rawRrn ? (
                  <div className="flex items-center gap-1.5">
                    <span className="font-bold text-slate-900">{formatResidentNumber(rawRrn, showResidentNumber)}</span>
                    {canToggleRrn && !isPrintMode && (
                      <button
                        type="button"
                        onClick={() => setShowResidentNumber(prev => !prev)}
                        className="px-1.5 py-0.5 bg-slate-100 hover:bg-indigo-50 border border-slate-300 hover:border-indigo-300 rounded text-[10px] font-bold text-slate-700 flex items-center gap-1 cursor-pointer transition-colors"
                        title={showResidentNumber ? '주민번호 마스킹 숨기기' : '주민번호 전체 확인 (관리자/본인 권한)'}
                      >
                        {showResidentNumber ? <EyeOff className="w-3 h-3 text-slate-500" /> : <Eye className="w-3 h-3 text-indigo-600" />}
                        <span>{showResidentNumber ? '마스킹' : '보기'}</span>
                      </button>
                    )}
                  </div>
                ) : (
                  <span className="text-[11px] text-amber-700 font-bold bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                    서명 시 본인 직접 기재 (필수)
                  </span>
                )}
              </td>
              <th className="bg-slate-100 p-2.5 text-slate-700 font-bold border-r border-slate-200">
                자택 주소
              </th>
              <td className="p-2.5 text-slate-800">
                {rawAddress || (
                  <span className="text-[11px] text-amber-700 font-medium bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                    서명 시 본인 직접 기재
                  </span>
                )}
              </td>
            </tr>
            <tr className="border-b border-slate-200">
              <th className="bg-slate-100 p-2.5 text-slate-700 font-bold border-r border-slate-200">
                {isFreelancer ? '전문 분야/역할' : '소속 부서 / 직급'}
              </th>
              <td className="p-2.5 text-slate-800 border-r border-slate-200">
                {contract.Department} {contract.Position ? `(${contract.Position})` : ''}
              </td>
              <th className="bg-slate-100 p-2.5 text-slate-700 font-bold border-r border-slate-200">
                이메일
              </th>
              <td className="p-2.5 text-slate-800 font-mono">
                {contract.UserEmail || '-'}
              </td>
            </tr>
            <tr className="border-b border-slate-200">
              <th className="bg-slate-100 p-2.5 text-slate-700 font-bold border-r border-slate-200">
                {isResignation ? '퇴사 희망일자' : '계약 기간'}
              </th>
              <td colSpan={3} className="p-2.5 text-slate-800 font-medium">
                {isResignation ? (
                  <span className="font-extrabold text-rose-700 text-sm">{contract.ResignationDate || contract.ContractEndDate || '-'}</span>
                ) : (
                  <>
                    <span className="font-bold text-indigo-700">{contract.ContractStartDate}</span> 부터{' '}
                    {contract.ContractEndDate ? (
                      <span className="font-bold text-indigo-700">{contract.ContractEndDate} 까지</span>
                    ) : (
                      <span className="text-slate-500 font-bold">기한의 정함이 없음 (상시 준수)</span>
                    )}
                  </>
                )}
              </td>
            </tr>
            {isResignation && (
              <>
                <tr className="border-b border-slate-200">
                  <th className="bg-slate-100 p-2.5 text-slate-700 font-bold border-r border-slate-200">
                    퇴직 사유
                  </th>
                  <td colSpan={3} className="p-2.5 font-bold text-slate-900">
                    {contract.ResignationReason || contract.JobDescription || '일신상의 사유'}
                  </td>
                </tr>
                {contract.HandoverRecipient && (
                  <tr className="border-b border-slate-200">
                    <th className="bg-slate-100 p-2.5 text-slate-700 font-bold border-r border-slate-200">
                      인수인계 대상자
                    </th>
                    <td colSpan={3} className="p-2.5 font-medium text-slate-800">
                      {contract.HandoverRecipient}
                    </td>
                  </tr>
                )}
              </>
            )}
            {contract.WorkingPlace && !isResignation && (
              <tr className="border-b border-slate-200">
                <th className="bg-slate-100 p-2.5 text-slate-700 font-bold border-r border-slate-200">
                  근무(수행) 장소
                </th>
                <td colSpan={3} className="p-2.5 text-slate-800">
                  {contract.WorkingPlace}
                </td>
              </tr>
            )}
            {contract.JobDescription && !isResignation && (
              <tr className="border-b border-slate-200">
                <th className="bg-slate-100 p-2.5 text-slate-700 font-bold border-r border-slate-200">
                  {isFreelancer ? '과업(용역) 범위' : isNda ? '서약 대상 업무' : '담당 업무'}
                </th>
                <td colSpan={3} className="p-2.5 text-slate-800 leading-relaxed whitespace-pre-wrap">
                  {contract.JobDescription}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Remuneration / Salary Section (Only rendered when hasCompensation is true) */}
      {hasCompensation && (
        <div className="mb-6">
          <h2 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <Building2 className="w-3.5 h-3.5 text-indigo-600" />
            제 2 장 : {isFreelancer ? '용역 대금 및 지급 조건' : '임금 및 지급 조건'}
          </h2>
          
          {isFreelancer ? (
            <div className="bg-indigo-50/40 border border-indigo-100 rounded-xl p-4 text-xs space-y-3">
              {/* Freelance Overview Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pb-2 border-b border-indigo-100">
                <div>
                  <span className="text-slate-500 font-bold block mb-0.5">총 계약 용역금액 (공급가액):</span>
                  <span className="text-base font-black text-slate-900 font-mono">
                    ₩ {formatWon(contract.TotalAmount)} 원
                  </span>
                </div>
                <div>
                  <span className="text-slate-500 font-bold block mb-0.5">비용 지급일 및 조건:</span>
                  <span className="text-xs font-bold text-indigo-700">
                    {contract.FreelancePaymentDate || '과업 검수 완료 후 7영업일 이내'}
                  </span>
                </div>
              </div>

              {/* Freelance Items Table if present */}
              {Array.isArray(contract.FreelanceItems) && contract.FreelanceItems.length > 0 && (
                <div className="space-y-1.5">
                  <span className="text-[11px] font-bold text-indigo-900 block">용역 대금 세부 내역:</span>
                  <table className="w-full text-xs border-collapse border border-indigo-200 bg-white rounded-lg overflow-hidden">
                    <thead className="bg-indigo-50/80 text-indigo-900 font-bold">
                      <tr>
                        <th className="p-2 text-left border-b border-indigo-200">과업 / 항목명</th>
                        <th className="p-2 text-right border-b border-indigo-200">공급가액</th>
                        <th className="p-2 text-left border-b border-indigo-200">비고</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contract.FreelanceItems.map((fItem, fIdx) => (
                        <tr key={fIdx} className="border-b border-slate-100 last:border-b-0">
                          <td className="p-2 font-bold text-slate-800">{fItem.name}</td>
                          <td className="p-2 font-mono font-bold text-right text-slate-900">₩ {formatWon(fItem.amount)} 원</td>
                          <td className="p-2 text-slate-500">{fItem.note || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Tax & Net Calculation Breakdown */}
              <div className="bg-white/80 p-3 rounded-xl border border-indigo-100 space-y-1.5">
                <div className="flex justify-between items-center text-slate-600">
                  <span>공급가액 합계:</span>
                  <span className="font-mono font-bold text-slate-900">₩ {formatWon(contract.TotalAmount)} 원</span>
                </div>
                {contract.FreelanceTaxMode === 'VAT_10' ? (
                  <div className="flex justify-between items-center text-indigo-700">
                    <span>+ 부가가치세 (10% 세금계산서):</span>
                    <span className="font-mono font-bold">₩ {formatWon(contract.TaxAmount || Math.floor(contract.TotalAmount * 0.1))} 원</span>
                  </div>
                ) : contract.FreelanceTaxMode === 'TAX_EXEMPT' ? (
                  <div className="flex justify-between items-center text-emerald-700">
                    <span>과세 구분:</span>
                    <span className="font-bold">면세 (0%)</span>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between items-center text-rose-600">
                      <span>- 사업소득세 원천징수 (3.0%):</span>
                      <span className="font-mono">₩ {formatWon(Math.floor(contract.TotalAmount * 0.03))} 원</span>
                    </div>
                    <div className="flex justify-between items-center text-rose-600">
                      <span>- 지방소득세 원천징수 (0.3%):</span>
                      <span className="font-mono">₩ {formatWon(Math.floor(contract.TotalAmount * 0.003))} 원</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between items-center pt-2 border-t border-indigo-100 text-indigo-900 font-black">
                  <span className="text-xs">
                    {contract.FreelanceTaxMode === 'VAT_10' ? '총 청구금액 (VAT 포함):' : '최종 실지급액 (Net Amount):'}
                  </span>
                  <span className="text-base font-black text-indigo-700 font-mono">
                    ₩ {formatWon(contract.NetAmount || (contract.FreelanceTaxMode === 'VAT_10' ? Math.floor(contract.TotalAmount * 1.1) : Math.floor(contract.TotalAmount * 0.967)))} 원
                  </span>
                </div>
              </div>

              <p className="text-[11px] text-slate-500">
                * 용역 대금은 {contract.FreelancePaymentDate || '최종 산출물 검수 완료 후 7영업일 이내'}에 수급인이 지정한 금융계좌로 전액 입금한다.
              </p>
            </div>
          ) : (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs space-y-3">
              {/* Regular Employee Salary Overview */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pb-3 border-b border-slate-200 text-center sm:text-left">
                <div className="bg-white p-2.5 rounded-xl border border-slate-200/80">
                  <span className="text-[10px] text-slate-500 font-bold block mb-0.5">연간 총 연봉 (세전)</span>
                  <span className="text-sm font-black text-slate-900 font-mono">
                    ₩ {formatWon(contract.AnnualSalary)} 원
                  </span>
                </div>
                <div className="bg-indigo-50/70 p-2.5 rounded-xl border border-indigo-100">
                  <span className="text-[10px] text-indigo-700 font-bold block mb-0.5">월 총지급액 (합산)</span>
                  <span className="text-sm font-black text-indigo-700 font-mono">
                    ₩ {formatWon(contract.MonthlySalary)} 원
                  </span>
                </div>
                <div className="bg-white p-2.5 rounded-xl border border-slate-200/80">
                  <span className="text-[10px] text-slate-500 font-bold block mb-0.5">급여 정기 지급일</span>
                  <span className="text-xs font-black text-slate-800">
                    {contract.SalaryPayday || '매달 말일'}
                  </span>
                </div>
              </div>

              {/* Detailed Salary Breakdown Table */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold text-slate-700">월급여 세부 구성 내역:</span>
                  <span className="text-[10px] text-slate-400 font-medium">* 비과세 수당은 관계 법령 한도 내 적용</span>
                </div>
                <table className="w-full text-xs border-collapse border border-slate-200 bg-white rounded-lg overflow-hidden">
                  <thead className="bg-slate-100 text-slate-700 font-bold">
                    <tr>
                      <th className="p-2 text-left border-b border-slate-200">급여 / 수당 항목</th>
                      <th className="p-2 text-right border-b border-slate-200">월 지급액</th>
                      <th className="p-2 text-center border-b border-slate-200">과세 / 비과세 구분</th>
                      <th className="p-2 text-left border-b border-slate-200">비고</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(Array.isArray(contract.SalaryItems) && contract.SalaryItems.length > 0) ? (
                      contract.SalaryItems.map((sItem, sIdx) => (
                        <tr key={sIdx} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/50">
                          <td className="p-2 font-bold text-slate-900">{sItem.name}</td>
                          <td className="p-2 font-mono font-bold text-right text-indigo-900">
                            ₩ {formatWon(sItem.amount)} 원
                          </td>
                          <td className="p-2 text-center">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                              sItem.isTaxFree ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'
                            }`}>
                              {sItem.isTaxFree ? '🌿 비과세(면세)' : '일반과세'}
                            </span>
                          </td>
                          <td className="p-2 text-slate-500">{sItem.note || '-'}</td>
                        </tr>
                      ))
                    ) : (
                      <>
                        <tr className="border-b border-slate-100">
                          <td className="p-2 font-bold text-slate-900">기본급</td>
                          <td className="p-2 font-mono font-bold text-right text-indigo-900">
                            ₩ {formatWon(Math.max(0, contract.MonthlySalary - 200000))} 원
                          </td>
                          <td className="p-2 text-center">
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-600">일반과세</span>
                          </td>
                          <td className="p-2 text-slate-500">기본급여</td>
                        </tr>
                        <tr className="border-b border-slate-100">
                          <td className="p-2 font-bold text-slate-900">식대</td>
                          <td className="p-2 font-mono font-bold text-right text-indigo-900">
                            ₩ {formatWon(Math.min(contract.MonthlySalary, 200000))} 원
                          </td>
                          <td className="p-2 text-center">
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800">🌿 비과세</span>
                          </td>
                          <td className="p-2 text-slate-500">비과세 (월 20만원 한도)</td>
                        </tr>
                      </>
                    )}
                  </tbody>
                  <tfoot className="bg-slate-50 font-bold border-t border-slate-200">
                    <tr>
                      <td className="p-2 text-slate-700">월 총합계 (월급여)</td>
                      <td className="p-2 font-mono text-right text-indigo-700 font-black">
                        ₩ {formatWon(contract.MonthlySalary)} 원
                      </td>
                      <td colSpan={2} className="p-2 text-right text-[11px] text-slate-600">
                        비과세 합계: <strong className="text-emerald-700">₩ {formatWon(contract.TotalTaxFree || (contract.SalaryItems ? contract.SalaryItems.filter(i=>i.isTaxFree).reduce((s,i)=>s+(Number(i.amount)||0),0) : 200000))} 원</strong>
                        <span className="mx-2 text-slate-300">|</span>
                        과세 대상액: <strong className="text-slate-800">₩ {formatWon(contract.TotalTaxable || (contract.SalaryItems ? contract.SalaryItems.filter(i=>!i.isTaxFree).reduce((s,i)=>s+(Number(i.amount)||0),0) : Math.max(0, contract.MonthlySalary - 200000)))} 원</strong>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Working hours & payment rule */}
              <div className="p-2.5 bg-white border border-slate-200 rounded-xl space-y-1 text-slate-700">
                <div>
                  <span className="font-bold text-slate-900">소정 근로시간: </span>
                  <span>{contract.WorkingHours || '주 5일, 1일 8시간 (09:00 ~ 18:00, 휴게 1시간)'}</span>
                </div>
                <div>
                  <span className="font-bold text-slate-900">급여 지급 방법: </span>
                  <span>{contract.SalaryPayday || '매달 말일'} 근로자가 지정한 본인 명의 금융계좌로 직접 입금한다. (지급일이 공휴일인 경우 그 전일 지급)</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Clauses Section */}
      <div className="mb-8 space-y-4">
        <h2 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5 text-indigo-600" />
          {hasCompensation ? '제 3 장' : '제 2 장'} : 상세 계약 조항 및 서약 사항
        </h2>

        {/* Standard Terms */}
        <div className="text-xs text-slate-700 space-y-2 leading-relaxed bg-white border border-slate-200 p-4 rounded-xl">
          {Array.isArray(contract.StandardClauses) && contract.StandardClauses.length > 0 ? (
            contract.StandardClauses.map((sc, scIdx) => (
              <p key={scIdx}>
                <strong>{sc.title} :</strong> {sc.content}
              </p>
            ))
          ) : (
            <>
              <p>
                <strong>제 1 조 (신의성실 및 법령 준수) :</strong> 양 당사자는 본 계약에서 정한 권리와 의무를 신의에 따라 성실히 이행하며, 관련 법령 및 사내 제반 규정을 엄격히 준수한다.
              </p>
              <p>
                <strong>제 2 조 (성실의무 및 겸업 금지) :</strong> {isFreelancer ? '수급인' : '근로자'}은 본 과업 수행 중 회사의 명예를 훼손하거나 업무에 지장을 초래하는 행위를 하여서는 아니 되며, 회사의 사전 승낙 없는 겸업을 금지한다.
              </p>
              <p>
                <strong>제 3 조 (비밀유지 및 지식재산권) :</strong> 본 계약 및 과업 수행 중 취득한 회사의 영업비밀, 고객 정보, 시스템 소스코드 등 일체의 기밀 정보는 계약 종료 후에도 3년간 외부에 누설하지 아니하며, 산출물의 지식재산권은 회사에 완전 귀속된다.
              </p>
            </>
          )}
        </div>

        {/* Custom Clause Blocks */}
        {Array.isArray(contract.CustomClauses) && contract.CustomClauses.length > 0 && (
          <div className="space-y-2">
            {contract.CustomClauses.map((clause, idx) => (
              <div key={idx} className="bg-indigo-50/40 border border-indigo-100 rounded-xl p-3.5 text-xs text-left">
                <div className="font-bold text-indigo-900 mb-1 flex items-center gap-1.5">
                  <span className="bg-indigo-200 text-indigo-800 text-[10px] px-1.5 py-0.5 rounded font-black">
                    특약 {idx + 1}
                  </span>
                  {clause.title}
                </div>
                <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{clause.content}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Handover Attachments Section */}
      {(() => {
        const attachedList = Array.isArray(contract?.AttachedFiles) && contract.AttachedFiles.length > 0 
          ? contract.AttachedFiles 
          : (Array.isArray(contract?.HandoverFiles) && contract.HandoverFiles.length > 0 
              ? contract.HandoverFiles 
              : (Array.isArray(contract?.Attachments) && contract.Attachments.length > 0 ? contract.Attachments : []));

        if (attachedList.length === 0) return null;

        return (
          <div className="mb-8 space-y-3">
            <h2 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
              <Paperclip className="w-3.5 h-3.5 text-indigo-600" />
              제 4 장 : 첨부된 업무 인수인계 산출물 및 증빙 자료 ({attachedList.length}건)
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {attachedList.map((file, fIdx) => (
                <div key={file.id || fIdx} className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex items-center justify-between gap-3 hover:bg-slate-100/80 transition-colors">
                  <div className="flex items-center gap-2.5 overflow-hidden">
                    <div className="w-8 h-8 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center shrink-0 font-bold">
                      <FileText className="w-4 h-4" />
                    </div>
                    <div className="overflow-hidden">
                      <div className="text-xs font-bold text-slate-900 truncate" title={file.name}>
                        {file.name}
                      </div>
                      <div className="text-[10px] text-slate-400 font-mono">
                        {file.size ? (file.size < 1024 * 1024 ? `${(file.size / 1024).toFixed(1)} KB` : `${(file.size / (1024 * 1024)).toFixed(1)} MB`) : '첨부파일'}
                      </div>
                    </div>
                  </div>

                  {file.dataUrl && (
                    <a
                      href={file.dataUrl}
                      download={file.name || '인수인계_첨부문서'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-2.5 py-1.5 bg-white hover:bg-indigo-50 border border-slate-300 hover:border-indigo-300 text-indigo-700 text-[11px] font-bold rounded-lg flex items-center gap-1 shrink-0 shadow-2xs transition-all cursor-pointer"
                    >
                      <Download className="w-3.5 h-3.5" />
                      다운로드
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Signature & Seal Block */}
      <div className="border-t-2 border-slate-900 pt-6 mb-6">
        <div className="text-center text-xs text-slate-600 mb-6 font-bold">
          본 계약의 성립을 증명하기 위하여 전자문서로 서명 날인하며, 각 당사자는 전자서명법에 따른 법적 효력을 인정한다.
        </div>

        <div className="grid grid-cols-2 gap-4 md:gap-8">
          {/* Company Seal */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs relative">
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">
              [회사 (사용자 / 발주처)]
            </span>
            <div className="space-y-1 text-slate-700">
              <div><strong>상호:</strong> {compName}</div>
              <div><strong>대표자:</strong> {repName}</div>
              <div><strong>주소:</strong> {compAddress}</div>
            </div>

            {/* Official Seal Image / Stamp */}
            <div className="mt-3 pt-3 border-t border-slate-200 flex items-center justify-between min-h-[56px]">
              <span className="text-[11px] font-bold text-slate-500">법인 직인 날인:</span>
              
              {sealImage ? (
                <img
                  src={sealImage}
                  alt="법인 직인"
                  className="h-14 max-w-[120px] object-contain rotate-[-4deg]"
                />
              ) : sealShape === 'OVAL' ? (
                <div className="w-16 h-14 rounded-[40%] border-2 border-rose-600 flex items-center justify-center text-rose-600 font-bold text-[10px] text-center leading-tight bg-rose-50/40 shadow-xs rotate-[-4deg] whitespace-pre-line">
                  {sealText}
                </div>
              ) : sealShape === 'SQUARE' ? (
                <div className="w-14 h-14 border-2 border-rose-600 flex items-center justify-center text-rose-600 font-bold text-[10px] text-center leading-tight bg-rose-50/40 shadow-xs rotate-[-3deg] whitespace-pre-line">
                  {sealText}
                </div>
              ) : (
                <div className="w-14 h-14 rounded-full border-2 border-rose-600 flex items-center justify-center text-rose-600 font-bold text-[10px] text-center leading-tight bg-rose-50/40 shadow-xs rotate-[-6deg] whitespace-pre-line">
                  {sealText}
                </div>
              )}
            </div>
            <div className="text-[10px] text-slate-400 mt-1 text-right font-mono">
              발행일자: {contract.CreatedAt ? contract.CreatedAt.slice(0, 10) : '-'}
            </div>
          </div>

          {/* Employee / Freelancer / Resignation Applicant Signature */}
          <div className={`border rounded-xl p-4 text-xs relative ${(contract.EmployeeSignatureData || contract.SignatureDataUrl || contract.Status === 'SIGNED') ? 'bg-emerald-50/40 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}>
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">
              [{isResignation ? '신청인 (퇴직자)' : isFreelancer ? '수급인 (전문 프리랜서)' : '근로자'}]
            </span>
            <div className="space-y-1 text-slate-700">
              <div><strong>성명:</strong> {contract.UserName}</div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <strong>주민번호:</strong>
                {rawRrn ? (
                  <span className="font-mono font-bold flex items-center gap-1.5">
                    {formatResidentNumber(rawRrn, showResidentNumber)}
                    {canToggleRrn && !isPrintMode && (
                      <button
                        type="button"
                        onClick={() => setShowResidentNumber(prev => !prev)}
                        className="px-1.5 py-0.5 bg-slate-100 hover:bg-indigo-50 border border-slate-300 hover:border-indigo-300 rounded text-[10px] font-bold text-slate-700 flex items-center gap-1 cursor-pointer transition-colors"
                        title={showResidentNumber ? '주민번호 마스킹 숨기기' : '주민번호 전체 확인 (관리자/본인 권한)'}
                      >
                        {showResidentNumber ? <EyeOff className="w-3 h-3 text-slate-500" /> : <Eye className="w-3 h-3 text-indigo-600" />}
                        <span>{showResidentNumber ? '마스킹' : '보기'}</span>
                      </button>
                    )}
                  </span>
                ) : (
                  <span className="text-[10px] text-amber-700 font-bold bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200">서명 시 본인 직접 기재 (필수)</span>
                )}
              </div>
              <div>
                <strong>주소:</strong>{' '}
                {rawAddress || <span className="text-[10px] text-amber-700 font-bold bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200">서명 시 본인 직접 기재 (필수)</span>}
              </div>
              <div><strong>연락처:</strong> {contract.UserPhone || '-'}</div>
              <div><strong>이메일:</strong> {contract.UserEmail || '-'}</div>
            </div>

            {/* Signature Area */}
            {(() => {
              const empSig = contract.EmployeeSignatureData || 
                             contract.SignatureDataUrl || 
                             contract.SignatureData || 
                             contract.SignatureUrl || 
                             contract.Signature || 
                             contract.EmployeeSignature || 
                             contract.signatureData || 
                             contract.signature || 
                             contract.AuditTrail?.EmployeeSignatureData ||
                             ((contract.Status === 'SIGNED' || contract.EmployeeSignedAt) ? `STAMP:${contract.UserName || '체결'}` : null);

              const isSigned = Boolean(empSig || contract.Status === 'SIGNED' || contract.EmployeeSignedAt);

              return (
                <div className="mt-3 pt-3 border-t border-slate-200 flex items-center justify-between min-h-[56px]">
                  <span className="text-[11px] font-bold text-slate-500">본인 서명 / 인감:</span>
                  
                  {isSigned ? (
                    <div className="flex items-center gap-2">
                      {empSig && empSig.startsWith('STAMP:') ? (
                        <div className="w-14 h-14 rounded-full border-2 border-rose-600 flex items-center justify-center text-rose-600 font-black text-xs bg-rose-50/60 shadow-xs rotate-[-4deg]">
                          {empSig.replace('STAMP:', '')}<br/>인
                        </div>
                      ) : empSig ? (
                        <img
                          src={empSig}
                          alt="Sign"
                          className="h-12 max-w-[120px] object-contain border-b-2 border-indigo-400"
                        />
                      ) : (
                        <div className="w-14 h-14 rounded-full border-2 border-rose-600 flex items-center justify-center text-rose-600 font-black text-xs bg-rose-50/60 shadow-xs rotate-[-4deg]">
                          {contract.UserName || '본인'}<br/>인
                        </div>
                      )}
                      <span className="text-[10px] text-emerald-700 font-bold bg-emerald-100/80 border border-emerald-200 px-1.5 py-0.5 rounded">
                        서명 완료
                      </span>
                    </div>
                  ) : (
                    <div className="text-amber-600 font-bold text-[11px] bg-amber-50 px-2 py-1 rounded border border-amber-200">
                      서명 대기 중 (Pending)
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="text-[10px] text-slate-400 mt-1 text-right font-mono">
              서명일시: {contract.EmployeeSignedAt ? new Date(contract.EmployeeSignedAt).toLocaleString('ko-KR') : (contract.SignedAt ? new Date(contract.SignedAt).toLocaleString('ko-KR') : '미체결')}
            </div>
          </div>
        </div>
      </div>

      {/* Resignation Multi-Step Approval Line Section */}
      {contract.IsResignation && Array.isArray(contract.ApprovalLine) && contract.ApprovalLine.length > 0 && (
        <div className="mb-6 bg-slate-50 border border-slate-200 rounded-2xl p-4 md:p-5 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-200 pb-3">
            <div className="flex items-center gap-2">
              <UserCheck className="w-4 h-4 text-indigo-600" />
              <h2 className="text-xs font-black text-slate-900">퇴사 전자결재 및 인수인계 승인선 (4단계)</h2>
            </div>
            <span className={`text-[10px] font-extrabold px-2.5 py-0.5 rounded-full ${
              contract.Status === 'APPROVED' ? 'bg-emerald-100 text-emerald-800' :
              contract.Status === 'REJECTED' ? 'bg-rose-100 text-rose-800' :
              contract.Status === 'WITHDRAWN' ? 'bg-amber-100 text-amber-800' :
              'bg-indigo-100 text-indigo-800'
            }`}>
              {contract.Status === 'APPROVED' ? '✓ 최종 퇴사 승인 완료' :
               contract.Status === 'REJECTED' ? '✕ 결재 반려됨' :
               contract.Status === 'WITHDRAWN' ? '↩ 퇴사 취하 (잔류 합의)' :
               `진행 중 (${contract.CurrentStep}단계 결재 대기)`}
            </span>
          </div>

          {/* 4-Step Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2.5">
            {contract.ApprovalLine.map((step) => {
              const isCurrent = step.Step === contract.CurrentStep && contract.Status !== 'APPROVED' && contract.Status !== 'REJECTED' && contract.Status !== 'WITHDRAWN';
              const isDone = step.Status === 'APPROVED';
              const isRejected = step.Status === 'REJECTED';

              return (
                <div 
                  key={step.Step}
                  className={`p-3 rounded-xl border text-xs flex flex-col justify-between space-y-2 transition-all ${
                    isCurrent ? 'bg-indigo-50/80 border-indigo-400 ring-2 ring-indigo-500/20 shadow-xs' :
                    isDone ? 'bg-emerald-50/60 border-emerald-200' :
                    isRejected ? 'bg-rose-50 border-rose-200' :
                    'bg-white border-slate-200 opacity-60'
                  }`}
                >
                  <div>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black text-slate-400">Step {step.Step}</span>
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                        isDone ? 'bg-emerald-200 text-emerald-900 font-extrabold' :
                        isCurrent ? 'bg-indigo-600 text-white font-extrabold animate-pulse' :
                        isRejected ? 'bg-rose-200 text-rose-900 font-extrabold' :
                        'bg-slate-100 text-slate-500'
                      }`}>
                        {isDone ? '승인 완료' : isCurrent ? '결재 진행 중' : isRejected ? '반려' : '대기'}
                      </span>
                    </div>
                    <div className="font-extrabold text-slate-900 mt-1">{step.RoleName}</div>
                    <div className="text-[11px] text-slate-600 font-medium">{step.ApproverName || '담당자'}</div>
                  </div>

                  <div className="pt-2 border-t border-slate-200/80 text-[10px] text-slate-500">
                    {step.SignedAt ? (
                      <div>
                        <div className="text-emerald-700 font-bold">✓ {new Date(step.SignedAt).toLocaleDateString()}</div>
                        {step.Comment && <div className="text-slate-600 italic truncate mt-0.5">"{step.Comment}"</div>}
                      </div>
                    ) : (
                      <span>{isCurrent ? '결재 대기' : '-'}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Interactive Approver Action Bar if current user can approve */}
          {(() => {
            const currentStepObj = contract.ApprovalLine.find(s => s.Step === contract.CurrentStep);
            const canApprove = Boolean(
              currentStepObj && 
              contract.Status !== 'APPROVED' && 
              contract.Status !== 'REJECTED' && 
              contract.Status !== 'WITHDRAWN' &&
              (currentStepObj.ApproverID === currentUser?.UserID || 
               currentUser?.Role === 'ADMIN' || 
               currentUser?.UserID === 'emp-10' || 
               currentUser?.UserID === 'emp-09')
            );

            if (!canApprove || isPrintMode || hideInlineApproval) return null;

            return (
              <div className="bg-white border-2 border-indigo-500/40 rounded-xl p-3.5 space-y-2.5 mt-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-indigo-900 flex items-center gap-1.5">
                    <Clock className="w-4 h-4 text-indigo-600" />
                    <strong>[{currentStepObj.RoleName}]</strong> 결재 승인 처리 대상자입니다.
                  </span>
                  <span className="text-[10px] bg-indigo-100 text-indigo-800 font-bold px-2 py-0.5 rounded">
                    결재 권한 확인됨
                  </span>
                </div>

                <input
                  type="text"
                  placeholder="결재 의견 또는 인수인계 확인 코멘트를 입력하세요 (선택)"
                  value={approvalComment}
                  onChange={(e) => setApprovalComment(e.target.value)}
                  className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-lg text-slate-800 focus:bg-white focus:outline-none focus:border-indigo-600"
                />

                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    disabled={isProcessing}
                    onClick={async () => {
                      const reason = prompt('반려 사유를 입력하세요:');
                      if (reason) {
                        setIsProcessing(true);
                        await rejectResignationStep(contract.ContractID, currentUser, reason);
                        setIsProcessing(false);
                      }
                    }}
                    className="px-3.5 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-700 font-bold rounded-lg border border-rose-200 cursor-pointer"
                  >
                    반려
                  </button>

                  <button
                    type="button"
                    disabled={isProcessing}
                    onClick={async () => {
                      setIsProcessing(true);
                      await approveResignationStep(contract.ContractID, currentUser, approvalComment);
                      setIsProcessing(false);
                      setApprovalComment('');
                    }}
                    className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg shadow-xs cursor-pointer flex items-center gap-1.5"
                  >
                    <Check className="w-3.5 h-3.5" />
                    <span>{contract.CurrentStep === 1 ? '인수인계 확인 완료' : '결재 승인'}</span>
                  </button>
                </div>
              </div>
            );
          })()}

          {/* Withdrawal Trigger Button */}
          {contract.Status !== 'APPROVED' && contract.Status !== 'REJECTED' && contract.Status !== 'WITHDRAWN' && onOpenWithdrawModal && !isPrintMode && (
            <div className="pt-2 border-t border-slate-200 flex justify-end">
              <button
                type="button"
                onClick={() => onOpenWithdrawModal(contract)}
                className="px-3 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer"
              >
                <RotateCcw className="w-3.5 h-3.5 text-amber-600" />
                퇴사 신청 취하 및 면담 일지 등록
              </button>
            </div>
          )}
        </div>
      )}

      {/* Withdrawal & Counseling Audit History Section */}
      {Array.isArray(contract.WithdrawalHistory) && contract.WithdrawalHistory.length > 0 && (
        <div className="mb-6 bg-amber-50/70 border border-amber-200 rounded-2xl p-4 md:p-5 space-y-3">
          <div className="flex items-center gap-2 text-amber-900 border-b border-amber-200 pb-2">
            <MessageSquare className="w-4 h-4 text-amber-700" />
            <h2 className="text-xs font-bold">퇴사 취하 히스토리 및 사원 면담 일지</h2>
          </div>

          {contract.WithdrawalHistory.map((item, idx) => (
            <div key={idx} className="bg-white border border-amber-200/80 rounded-xl p-3.5 text-xs space-y-2 shadow-2xs">
              <div className="flex items-center justify-between text-[11px] text-slate-500">
                <span>면담일자: <strong className="text-slate-900 font-bold">{item.counselingDate}</strong></span>
                <span>상담 진행자: <strong className="text-amber-800 font-bold">{item.counselorName}</strong></span>
              </div>
              <div>
                <span className="font-bold text-slate-700">취하 및 잔류 사유: </span>
                <span className="text-slate-900 font-semibold">{item.reason}</span>
              </div>
              <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-100">
                <span className="text-[10px] font-bold text-slate-400 block mb-1">상담 및 면담 상세 기록</span>
                <p className="text-slate-700 leading-relaxed whitespace-pre-wrap text-[11px]">{item.interviewNotes}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Immutable Security Audit Trail Banner */}
      {contract.Status === 'SIGNED' && contract.AuditTrail && (
        <div className="bg-slate-900 text-slate-300 rounded-xl p-3.5 text-[11px] font-mono border border-slate-800">
          <div className="flex items-center gap-2 text-emerald-400 font-bold text-xs mb-1">
            <ShieldCheck className="w-4 h-4" />
            공식 전자계약 인증 감사 로그 (Immutable Audit Trail)
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-slate-400 text-[10px]">
            <div>• 서명자 인증: {contract.UserName} ({contract.UserEmail || contract.UserPhone})</div>
            <div>• 체결 일시(KST): {new Date(contract.EmployeeSignedAt).toLocaleString('ko-KR')}</div>
            <div>• 서명 IP: {contract.AuditTrail.IpAddress}</div>
            <div>• 디바이스: {contract.AuditTrail.UserAgent ? contract.AuditTrail.UserAgent.slice(0, 45) + '...' : '-'}</div>
            <div className="md:col-span-2 text-emerald-300/90 break-all font-mono">
              • SHA-256 문서 무결성 지문: {contract.AuditTrail.Sha256Hash || 'SHA256: 7f9a12c8...verified'}
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default ContractDocumentView;
